import { withNamespaces } from "react-i18next";
import {
    Modal,
    ModalHeader,
    ModalBody,
    Col,
    Row,
    Button,
    FormGroup,
    Label,
    ModalFooter,
    Spinner,
} from "reactstrap";
import { AvForm, AvField } from "availity-reactstrap-validation";
import DataProtectionTomService from "src/modules/data-protection/apis/DataProtectionTomService";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";

const SubContractorModal = (props) => {
    const { t, modal, setModal, type, handleFetchDetails, data } = props;

    const id = data?.id;

    const service = DataProtectionTomService.getInstance();

    const toggle = () => setModal(!modal);

    const editSubcontractorMutation = useMutation({
        mutationFn: async (data) => {
            return await service.editSubcontractor(data, id);
        },
        onSuccess: () => {
            toast(t("Subcontractor edited successfully."), {
                type: "success",
            });
            handleFetchDetails.refetch();
            toggle();
        },
        onError: () => {
            toast(t("An error occurred while editing the subcontractor."), {
                type: "error",
            });
        },
    });

    const createSubcontractorMutation = useMutation({
        mutationFn: async (data) => {
            return await service.createSubcontractor(data);
        },
        onSuccess: () => {
            toast(t("New subcontractor created successfully."), {
                type: "success",
            });
            handleFetchDetails.refetch();
            toggle();
        },
        onError: () => {
            toast(t("An error occurred while creating the subcontractor."), {
                type: "error",
            });
        },
    });
    
    const handleSubmit = (_e, values) => {
        if(type === 'edit'){
            editSubcontractorMutation.mutate(values)
        }
        else{
            createSubcontractorMutation.mutate(values)
        }
    }

    const formIsLoading = editSubcontractorMutation.isLoading || createSubcontractorMutation.isLoading;

    return (
        <div>
            <Modal isOpen={modal} toggle={toggle} size="xl">
                <AvForm
                    className="needs-validation"
                    onValidSubmit={handleSubmit}
                >
                    <ModalHeader toggle={toggle}>
                        {type === "create" ? t("Add Subcontractor") : t("Edit Subcontractor")}
                    </ModalHeader>
                    
                    <ModalBody>
                        <Row className="mb-4">
                            <Col md="4" sm="12">
                                <FormGroup>
                                    <Label for="area">
                                        {t("Area")}
                                    </Label>
                                    <AvField
                                        name="area"
                                        id="area"
                                        type="text"
                                        errorMessage={t("This field cannot be blank")}
                                        className="form-control"
                                        validate={{
                                            required: { value: true }
                                        }}
                                        defaultValue={data?.area}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="4" sm="12">
                                <FormGroup>
                                    <Label for="name">
                                        {t("Name")}
                                    </Label>
                                    <AvField
                                        name="name"
                                        id="name"
                                        type="text"
                                        errorMessage={t("This field cannot be blank")}
                                        className="form-control"
                                        validate={{
                                            required: { value: true }
                                        }}
                                        defaultValue={data?.name}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="4" sm="12">
                                <FormGroup>
                                    <Label for="address">
                                        {t("Address")}
                                    </Label>
                                    <AvField
                                        name="address"
                                        id="address"
                                        type="text"
                                        errorMessage={t("This field cannot be blank")}
                                        className="form-control"
                                        validate={{
                                            required: { value: true }
                                        }}
                                        defaultValue={data?.address}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col md="4" sm="12">
                                <FormGroup>
                                    <Label for="website">
                                        {t("Website")}
                                    </Label>
                                    <AvField
                                        name="website"
                                        id="website"
                                        type="text"
                                        errorMessage={t("This field cannot be blank")}
                                        className="form-control"
                                        validate={{
                                            required: { value: true },
                                            pattern: {
                                                value: /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/,
                                                errorMessage: t("Please enter a valid URL"),
                                            }
                                        }}
                                        defaultValue={data?.website}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="4" sm="12">
                                <FormGroup>
                                    <Label for="GTC">
                                        {t("GTC")}
                                    </Label>
                                    <AvField
                                        name="GTC"
                                        id="GTC"
                                        type="text"
                                        errorMessage={t("This field cannot be blank")}
                                        className="form-control"
                                        validate={{
                                            required: { value: true },
                                            pattern: {
                                                value: /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/,
                                                errorMessage: t("Please enter a valid URL"),
                                            }
                                        }}
                                        defaultValue={data?.g_t_c}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="4" sm="12">
                                <FormGroup>
                                    <Label for="privacyPolicy">
                                        {t("Privacy Policy")}
                                    </Label>
                                    <AvField
                                        name="privacyPolicy"
                                        id="privacyPolicy"
                                        type="text"
                                        errorMessage={t("This field cannot be blank")}
                                        className="form-control"
                                        validate={{
                                            required: { value: true },
                                            pattern: {
                                                value: /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/,
                                                errorMessage: t("Please enter a valid URL"),
                                            }
                                        }}
                                        defaultValue={data?.privacy_policy}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                    </ModalBody>

                    <ModalFooter>
                        <Button color="secondary" outline onClick={toggle}>
                            {t("Cancel")}
                        </Button>
                        <Button
                            color="primary"
                            type="submit"
                            disabled={formIsLoading}
                        >
                            {formIsLoading ? (
                                <>
                                    <Spinner 
                                        size='sm'
                                        color="white"
                                        className="me-1"
                                    />
                                    {t("Saving")}...
                                </>
                            ) : (
                                <>
                                    {t("Save")}
                                </>
                            )}
                        </Button>
                    </ModalFooter>
                </AvForm>
            </Modal>
        </div>
    );
};

export default withNamespaces()(
    SubContractorModal
);
